// src/pages/legal/TermsOfService.js
//
// MERGED TERMS — your 2022 Terms of Use is the base; the new commercial
// sections are folded in.
//
// ===========================================================================
// WHAT I DID WITH YOUR TWO DOCUMENTS
// ===========================================================================
//
// SBL_Terms_of_Use_2022.docx — USED AS THE BASE.
//   This is a real, properly drafted, SBL-branded agreement. It already
//   contains things my earlier draft did not: a JAMS arbitration clause, a
//   class-action and jury-trial waiver, a DMCA designated agent, FERPA
//   Student Data provisions, export controls, and a detailed acceptable-use
//   policy. Its structure and its operative language are preserved here.
//
// SBL_Terms_and_Conditions_1.docx — NOT USED. See the warning below.
//
// ===========================================================================
// THE GAP YOUR 2022 TERMS HAVE
// ===========================================================================
// Searched it for "refund", "subscription", "renew", "billing", "trial".
// There is nothing. The 2022 document was written for a product that didn't
// charge a recurring fee, so it has no payment terms at all — no
// non-refundable clause, no auto-renewal disclosure, no trial terms.
//
// That's the single biggest hole, and it's exactly what you asked to close.
// Sections 4, 5 and 6 below are new and fill it.
//
// Also new, because 2022 didn't contemplate them:
//   §8  educator-authored content licence (what lets you publish teacher
//       questions commercially)
//   §9  no warranty of accuracy for topics, tools, scores and standards
//       alignment
//
// ===========================================================================
// GOVERNING LAW — ONE THING TO CHECK WITH COUNSEL
// ===========================================================================
// Your 2022 document specifies New York law with venue in Washington, D.C.,
// for a Virginia LLC with a Virginia mailing address. That combination is
// unusual. It's preserved verbatim here because changing your governing-law
// clause on my own judgement would be the wrong call — but ask your attorney
// whether it was deliberate. If it came from a template, Virginia law and
// Virginia venue would be the ordinary choice for a Virginia company.

import React from "react";
import { Link } from "react-router-dom";
import { TERMS_VERSION, TERMS_EFFECTIVE_DATE, COMPANY } from "../../config/terms";

export default function TermsOfService() {
  return (
    <div style={s.page}>
      <article style={s.sheet}>
        <header style={s.header}>
          <h1 style={s.h1}>Terms of Service</h1>
          <p style={s.meta}>
            Effective {TERMS_EFFECTIVE_DATE} · Version {TERMS_VERSION}
          </p>
          <p style={s.notice}>
            <strong>Please read carefully.</strong> These Terms require most
            disputes to be resolved by binding arbitration on an individual
            basis rather than by jury trial or class action, and they limit the
            remedies available to you. See Section 17.
          </p>
          <p style={s.lede}>
            This agreement sets out the legally binding terms for your use of
            any {COMPANY.shortName} application, website, or related service
            (the "Products"). By creating an account, starting a free trial, or
            making a purchase, you accept these Terms and represent that you
            have the right, authority, and capacity to enter into them. If you
            do not agree, please do not use the Products.
          </p>
        </header>

        <Section n="1" title="Terms and accounts">
          <p>
            You are solely responsible for the way you use the Products. Your{" "}
            {COMPANY.shortName} account grants you access to the features and
            capabilities we build and maintain at our discretion from time to
            time. We may keep different types of accounts for different sorts
            of users.
          </p>
          <p>
            If you are accepting these Terms and using the Products on behalf
            of a company, organization, government, school, or other legal
            entity, then "you" refers to both you and that entity, and you
            represent and warrant that you are an authorized representative
            with authority to bind it.
          </p>
          <p>
            If you have allowed or registered another individual, including a
            minor, to use your account, you are solely responsible for that
            user's online conduct, for controlling their access to and use of
            the Products, and for the consequences of any misuse. A minor's
            parent, guardian, other authorized adult, or educational
            institution must explicitly consent to the minor's use of a{" "}
            {COMPANY.shortName} Product. On request from a minor's parent,
            guardian, other authorized adult, or educational institution,{" "}
            {COMPANY.shortName} reserves the right to grant access to that
            minor's account.
          </p>
          <p>
            Your interactions with other users are your responsibility. We
            reserve the right to monitor disagreements between users but have
            no duty to do so, and we disclaim responsibility for any user's
            conduct or omissions.
          </p>
          <p>
            You may not share a single paid account among multiple educators,
            classrooms, or households. Seats are per person. Educator codes
            identify your classroom, and anyone holding your code can join your
            roster — share it only with your own students and their families,
            and tell us promptly if you believe it has been misused.
          </p>
        </Section>

        <Section n="2" title="Privacy and use of personal information">
          <p>
            Any information you provide to {COMPANY.shortName} — including
            names, email addresses, and any other personal information you
            provide or that we collect in connection with your use of the
            Products ("Your Information") — is collected, maintained, and used
            in accordance with these Terms and our{" "}
            <Link to="/privacy-policy" style={s.link}>
              Privacy Policy
            </Link>
            . By using the Products you agree to that collection and use, and
            to the transfer of Your Information to the United States and other
            countries for storage, hosting, and processing.
          </p>
          <p>
            Personal information provided through the Products, including
            Student Data, is governed by the agreement between{" "}
            {COMPANY.shortName} and the educational institution applicable to
            your use of the Products. You and/or the educational institution
            own all right, title, and interest in all Student Data you provide
            or make available to us. We do not own, control, or license Student
            Data except as necessary to provide the Products.
          </p>
          <p>
            "Student Data" means any information, in any format, directly
            related to an identifiable current or former student that is
            maintained by a school, school district, related person or
            organization, or by us as part of supplying the Products. Student
            Data may include "educational records" as defined by the Family
            Educational Rights and Privacy Act ("FERPA"), 20 U.S.C. § 1232(g).{" "}
            {COMPANY.shortName} pledges to keep Student Data private and not to
            share it with third parties except as specified in the educational
            institution's agreement with {COMPANY.shortName}.
          </p>
          <p>
            Please do not enter student names, images, or other identifying
            details into quiz questions, uploads, or other free-text fields.
          </p>
          <p>
            Questions about the privacy and use of Your Information:{" "}
            <a href="mailto:privacy@smartboardlearning.com" style={s.link}>
              privacy@smartboardlearning.com
            </a>{" "}
            or your educational institution.
          </p>
        </Section>

        <Section n="3" title="Licence to use the Products">
          <p>
            Subject to these Terms and any agreement entered into by you, your
            institution, or you as a parent, {COMPANY.shortName} grants you a
            personal, worldwide, royalty-free, non-assignable, non-exclusive
            licence to use the Products solely for their intended use — and not
            for competitive intelligence, analysis, or demonstrations. Each
            licence is for single use and cannot be transferred.
          </p>
          <p style={s.callout}>
            <strong>
              Unless the law prohibits such restrictions or you have our prior
              written consent, you may not copy, reproduce, download, record,
              screen-capture, rip, re-upload, modify, distribute, republish,
              publicly perform, broadcast, sell, or lease any portion of the
              Products, nor reverse engineer or attempt to extract the source
              code of any Product.
            </strong>
          </p>
          <p>
            This applies to all material available through the Products,
            including songs, lyrics, recordings, videos, animations, images,
            games, quizzes, worksheets, lesson material, characters, artwork,
            software, and the selection and arrangement of all of it.
          </p>
          <p>When using the Products you must not:</p>
          <ul style={s.list}>
            <li>
              bypass, deactivate, or otherwise interfere with any security
              feature, or any feature that prevents or restricts the use or
              copying of content;
            </li>
            <li>
              use automated tools to scrape, crawl, harvest, or bulk-download
              content or media files;
            </li>
            <li>
              create multiple accounts for use with a single product, or
              provide incorrect or misleading information;
            </li>
            <li>
              impersonate any person, misrepresent your affiliation, or give
              the impression you are connected to {COMPANY.shortName};
            </li>
            <li>use a Product after being suspended or banned from it;</li>
            <li>
              send spam, unsolicited advertising, or engage in activity
              violating the CAN-SPAM Act, the Telephone Consumer Protection
              Act, or similar laws;
            </li>
            <li>
              advocate, encourage, or engage in unlawful conduct, or conduct
              that harms any person or property;
            </li>
            <li>
              modify, interfere with, intercept, disrupt, or hack any Product,
              or upload viruses, worms, or malicious code;
            </li>
            <li>
              collect data about other users without their consent, or attempt
              unauthorized access to our servers or networks;
            </li>
            <li>
              use the Products or their content to build, train, or evaluate
              any machine-learning or artificial-intelligence system; or
            </li>
            <li>
              remove or obscure any copyright notice, watermark, or
              attribution.
            </li>
          </ul>
          <p>
            Requests for written permission go to{" "}
            <a href={`mailto:${COMPANY.email}`} style={s.link}>
              {COMPANY.email}
            </a>
            . Permission is not granted unless given in writing by an
            authorized representative.
          </p>
        </Section>

        <Section n="4" title="Subscriptions, billing, and automatic renewal" isNew>
          <p>
            Paid plans are billed in advance on a recurring basis — monthly or
            annually, as shown at checkout. Payments are processed by Stripe;
            we do not store your full card details.
          </p>
          <p>
            <strong>
              Your subscription renews automatically at the end of each billing
              period at the then-current rate
            </strong>{" "}
            until you cancel. You may cancel at any time from your account
            settings or by contacting us. Cancellation stops future renewals
            and takes effect at the end of the period already paid for.
          </p>
          <p>
            Where a discount applies for a limited number of billing periods,
            the standard rate applies automatically once that promotional
            period ends. We may revise pricing at any time; changes apply from
            your next renewal.
          </p>
          <p>
            You agree to pay all fees and applicable taxes incurred by you or
            anyone making purchases through your account.
          </p>
          <LegalFlag>
            Auto-renewal disclosure and cancellation mechanics are regulated,
            and California's Automatic Renewal Law is the strictest — it
            carries statutory penalties and has specific requirements for how
            and where consent is captured at checkout. Your 2022 Terms had no
            payment section at all, so this is entirely new language. Have
            counsel review it alongside your actual checkout screen.
          </LegalFlag>
        </Section>

        <Section n="5" title="Payments are non-refundable" isNew>
          <p style={s.callout}>
            <strong>All payments are final and non-refundable.</strong> This
            includes subscription fees, renewal charges, seat purchases, and
            any partial billing period.
          </p>
          <p>
            You acknowledge that {COMPANY.shortName} is not required to provide
            a refund for any reason, and that you will not receive money or
            other compensation for unused access, unused seats, or unused
            virtual items when you close your account or cease using the
            Products. We do not provide refunds, credits, or prorated amounts
            for periods you did not use, for accounts you stopped using, for
            seats you assigned and later revoked, or for cancellation partway
            through a paid term.
          </p>
          <p>
            Because a free trial is offered so that you can evaluate the
            Products before paying, we expect you to use it for that purpose.
          </p>
          <p>
            If we withdraw the Products entirely during a period you have paid
            for, we may at our sole discretion offer a prorated credit. Nothing
            in this section limits any refund right you have under applicable
            law that cannot be waived by agreement.
          </p>
          <LegalFlag>
            A flat no-refunds term is not fully enforceable everywhere.
            Consumer-protection rules in several U.S. states and in the EU/UK
            override it, and card-network chargeback rules operate regardless
            of what this page says. The final sentence is the savings clause —
            do not remove it without advice.
          </LegalFlag>
        </Section>

        <Section n="6" title="Free trials, extensions, and promotional codes" isNew>
          <p>
            We may offer a free trial, and we may extend a trial or offer a
            discount at our discretion. Trials, extensions, and discounts are a
            courtesy, not an entitlement: we may change, shorten, or withdraw
            them at any time, and we may limit them to one per person,
            household, school, or payment method.
          </p>
          <p>
            Free trials may be subject to limits on the number of student
            accounts that can be linked to them. When a trial ends, access to
            paid features stops unless you have started a paid subscription.
            Content and progress created during a trial may become read-only or
            unavailable.
          </p>
          <p>
            Promotional and discount codes are issued for a limited time, may be
            withdrawn or deactivated without notice, cannot be exchanged for
            cash, and may not be resold. We may cancel any order or
            subscription obtained through a code that was misused, resold, or
            used outside its intended audience.
          </p>
        </Section>

        <Section n="7" title="Content you submit">
          <p>
            You are solely responsible for the Content you publish to the
            Products and for any consequences. Other users may see Content you
            submit, upload, or display.
          </p>
          <p>
            Any Content you submit, post, or display on or through the Products
            remains your property. You grant us a worldwide, non-exclusive,
            royalty-free licence (with the right to sublicense) to use, host,
            store, copy, reproduce, process, adapt, modify, publish, transmit,
            create derivative works from, communicate, display, and distribute
            such Content in any and all media or distribution methods, now known
            or later developed, as part of providing the Products. You agree
            that as part of providing the Products — including after termination
            of your use of them — {COMPANY.shortName} has the right to provide,
            promote, and improve the Products, and to make Content submitted
            through the Products available to other institutions or individuals
            in accordance with your privacy settings.
          </p>
          <p>
            In connection with Content you submit, you represent and warrant
            that: (a) you have obtained written approval from each natural
            person identified in the Content to use their name or likeness, and
            each has released you from any resulting responsibility; (b) you
            have obtained all consents necessary by law to publish Content
            relating to third parties; (c) your Content, and our use of it as
            contemplated by these Terms, will not break any law or infringe any
            third-party right, including intellectual property and privacy
            rights; and (d) we may exploit the rights granted without incurring
            liability for guild fees, residuals, payments, or royalties under
            any collective bargaining or licensing agreement.
          </p>
          <p>
            We may, but are not obliged to, review Content. We may remove or
            modify Content at any time, for any reason, with or without notice.
            We are not obliged to back up Content, and Content may be deleted at
            any time — keep your own copies if you need them.
          </p>
        </Section>

        <Section n="8" title="Educator-authored questions and materials" isNew>
          <p>
            The Products may let educators and other users submit quiz
            questions and answers, topic suggestions, and similar teaching
            material ("Educator Content"). You keep ownership of your Educator
            Content.
          </p>
          <p style={s.callout}>
            By submitting Educator Content you grant {COMPANY.shortName} a
            worldwide, perpetual, irrevocable, royalty-free, sublicensable, and
            transferable licence to use, host, store, reproduce, modify, adapt,
            translate, publish, publicly perform, publicly display, and
            distribute it, and to create derivative works from it, in any media
            now known or later developed, for any purpose connected with
            operating, improving, or promoting the Products —{" "}
            <strong>
              including incorporating it into our commercial content library and
              making it available to other users and institutions
            </strong>
            .
          </p>
          <p>
            You confirm that you own or otherwise have all rights necessary to
            grant this licence; that your Educator Content is original to you
            and does not copy any textbook, workbook, assessment, publisher
            material, or other third-party work; that it does not infringe
            anyone's rights; and that it contains no personal information about
            any student and nothing unlawful, misleading, or inappropriate for
            children.
          </p>
          <p>
            We are not obliged to use, publish, credit, or retain any Educator
            Content, and we may review, edit, reject, or remove it at any time.
            We do not pay for Educator Content unless we agree otherwise in
            writing.
          </p>
          <LegalFlag>
            This is the clause that lets you publish teacher-written questions
            commercially. Your 2022 Terms had a general Content licence
            (Section 7 above), but it is scoped to "as part of providing the
            Products" — which is narrower than incorporating submissions into a
            product you sell. That's why this section is separate and broader.
            Ask counsel to confirm the breadth, and whether you want to offer
            attribution or an opt-out. Much cheaper to settle now than after
            four thousand questions are collected.
          </LegalFlag>
        </Section>

        <Section n="9" title="No guarantee of accuracy; educational judgement" isNew>
          <p>
            We work to keep our material accurate and aligned to relevant
            standards, but{" "}
            <strong>
              we do not warrant that any topic, lesson, video, song, quiz
              question, answer, score, standards alignment, or tool is
              accurate, complete, current, or suitable for any particular
              student, curriculum, or assessment
            </strong>
            . Educational standards change, and material may not reflect the
            most recent revision in your state or district.
          </p>
          <p>
            The Products are a supplement to teaching, not a substitute for the
            professional judgement of an educator or the involvement of a
            parent. Scores, progress indicators, stars, play counts, and
            classroom reports are engagement signals for your own use. They are
            not assessments, grades, diagnoses, or evaluations of ability, and
            they must not be used as the sole basis for any decision about a
            student's placement, instruction, or evaluation.
          </p>
          <p>
            You are responsible for reviewing material before using it with
            students and for confirming it meets your curriculum requirements.
            If you find an error, please tell us — we want to fix it.
          </p>
        </Section>

        <Section n="10" title="Our rights and intellectual property">
          <p>
            The Products, and all intellectual property rights in them, are and
            remain the property of {COMPANY.shortName} and its licensors. These
            Terms are not a sale and convey no ownership rights. The{" "}
            {COMPANY.shortName} name, logo, domain names, and other brand
            elements belong to us, and no right or licence to use them is
            granted by implication, estoppel, or otherwise. We reserve all
            rights not expressly granted.
          </p>
          <p>
            If you provide feedback, comments, or suggestions, you assign to us
            all rights in that feedback and agree we may use it in any manner we
            consider appropriate, without obligation to you. We will treat
            feedback as non-confidential and non-proprietary, so please do not
            send us anything you consider confidential.
          </p>
        </Section>

        <Section n="11" title="Copyright complaints (DMCA)">
          <p>
            We respect intellectual property rights and expect users of the
            Products to do the same. We will respond to properly delivered
            notices of alleged copyright infringement in accordance with
            applicable law.
          </p>
          <p>
            If you believe your work has been copied in a way that constitutes
            infringement, please send our designated agent: (i) a physical or
            electronic signature of the copyright owner or a person authorised
            to act on their behalf; (ii) identification of the copyrighted work
            claimed to be infringed; (iii) identification of the material
            claimed to be infringing and information sufficient to let us locate
            it; (iv) your address, telephone number, and email address; (v) a
            statement that you have a good-faith belief that the use is not
            authorised by the copyright owner, its agent, or the law; and (vi) a
            statement, under penalty of perjury, that the information in the
            notice is accurate and that you are authorised to act on the
            copyright owner's behalf.
          </p>
          <address style={s.address}>
            {COMPANY.legalName}
            <br />
            Attn: SBL Counsel
            <br />
            PO Box 22
            <br />
            Ashburn, VA 20146
            <br />
            <a href="mailto:SBLCounsel@smartboardlearning.com" style={s.link}>
              SBLCounsel@smartboardlearning.com
            </a>
          </address>
          <p>
            We may remove Content we deem infringing, at our discretion and
            without obligation to you, and we may terminate the account of any
            user who repeatedly infringes third-party copyright.
          </p>
        </Section>

        <Section n="12" title="Registration and passwords">
          <p>
            You are responsible for maintaining the confidentiality of your
            login details and for all activity under your account. You agree to
            provide accurate registration information and to keep it current.
            Notify us immediately of any unauthorised use of your account.
          </p>
        </Section>

        <Section n="13" title="Export controls">
          <p>
            You represent and warrant that you are not located in a country
            subject to a U.S. Government embargo or designated as a
            "terrorist-supporting" country, and that you are not listed on any
            U.S. Government list of prohibited or restricted parties. You are
            responsible for compliance with all applicable laws, including
            export and import regulations.
          </p>
        </Section>

        <Section n="14" title="Suspension and termination">
          <p>
            These Terms commence when you accept them — or, if you used the
            Products before accepting them, when you first used them — and
            remain in force while you use the Products.
          </p>
          <p>
            We may suspend your rights to use the Products, or terminate these
            Terms, at any time and at our discretion, with or without notice,
            including where we believe in good faith that you have breached the
            acceptable-use provisions or any other provision of these Terms.
            Where practical we will tell you first.
          </p>
          <p>
            On termination your right to use the Products ends immediately.
            Termination may involve deletion of your Content from our live
            databases. Termination for breach does not entitle you to a refund
            — see Section 5.
          </p>
          <p>
            Sections 3, 5, 7, 8, 9, 10, 15, 16, 17 and 18 survive termination.
          </p>
        </Section>

        <Section n="15" title="Disclaimers">
          <p style={s.legalBlock}>
            THE PRODUCTS ARE PROVIDED "AS IS" AND "AS AVAILABLE", AND{" "}
            {COMPANY.shortName.toUpperCase()} AND ITS SUPPLIERS EXPRESSLY
            DISCLAIM ALL WARRANTIES AND CONDITIONS OF ANY KIND, WHETHER EXPRESS,
            IMPLIED, OR STATUTORY, INCLUDING THE WARRANTIES OR CONDITIONS OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, QUIET
            ENJOYMENT, ACCURACY, AND NON-INFRINGEMENT. WE MAKE NO WARRANTY THAT
            THE PRODUCTS: (A) WILL MEET YOUR REQUIREMENTS; (B) WILL BE AVAILABLE
            ON AN UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE BASIS; OR (C)
            WILL BE ACCURATE, RELIABLE, COMPLETE, LEGAL, OR SAFE.
          </p>
          <p>
            SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED
            WARRANTIES, SO THE ABOVE EXCLUSION MAY NOT APPLY TO YOU.
          </p>
        </Section>

        <Section n="16" title="Limitation of liability and indemnity">
          <p style={s.legalBlock}>
            IN NO EVENT WILL {COMPANY.shortName.toUpperCase()} OR ITS SUPPLIERS
            BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY LOST PROFIT OR ANY
            INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE
            DAMAGES ARISING FROM OR RELATING TO THESE TERMS OR THE PRODUCTS,
            EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. ACCESS TO AND
            USE OF THE PRODUCTS IS AT YOUR OWN DISCRETION AND RISK.
          </p>
          <p style={s.legalBlock}>
            NOTWITHSTANDING ANYTHING TO THE CONTRARY, OUR TOTAL LIABILITY TO YOU
            FOR ANY DAMAGES ARISING FROM OR RELATED TO THESE TERMS OR OUR
            PRIVACY PRACTICES, FOR ANY CAUSE AND REGARDLESS OF THE FORM OF
            ACTION, WILL AT ALL TIMES BE LIMITED TO THE AMOUNT YOU HAVE PAID US
            IN THE PRIOR TWELVE (12) MONTHS, IF ANY.
          </p>
          <p>
            SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR EXCLUSION OF
            LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE MAY
            NOT APPLY TO YOU, AND YOU MAY HAVE OTHER RIGHTS THAT VARY BY
            JURISDICTION.
          </p>
          <p>
            You agree to defend, indemnify, and hold harmless{" "}
            {COMPANY.shortName} and its suppliers from any claims, suits,
            losses, damages, liabilities, costs, and expenses (including
            reasonable attorneys' fees) brought by third parties arising from or
            relating to your access to or use of the Products, your Content, or
            your violation of these Terms. We reserve the right, at your
            expense, to assume the exclusive defence and control of any matter
            for which you must indemnify us, and you agree to cooperate. You may
            not settle any such matter without our prior written consent.
          </p>
        </Section>

        <Section n="17" title="Governing law and dispute resolution">
          <p>
            <strong>Governing law.</strong> The laws of the State of New York
            govern these Terms and any action taken in connection with them,
            without respect to conflict-of-law provisions or the laws of your
            state or country of residence. All claims, legal processes, or
            litigation arising out of or relating to the Products will be
            conducted exclusively in federal or state courts in Washington,
            D.C., and you consent to the jurisdiction and venue of those courts,
            waiving any inconvenient-forum objection.
          </p>
          <p style={s.legalBlock}>
            <strong>Arbitration.</strong> READ THIS CAREFULLY — IT REQUIRES THE
            PARTIES TO ARBITRATE DISPUTES AND LIMITS THE MANNER IN WHICH RELIEF
            CAN BE SOUGHT. If you have a problem with {COMPANY.shortName}, you
            agree to contact us at SBLCounsel@smartboardlearning.com first and
            try to settle it informally. If we are unable to resolve a dispute
            with you after sixty (60) days, we each agree to resolve any claim,
            dispute, or controversy arising out of or relating to these Terms —
            excluding claims for injunctive or other equitable relief — by
            binding arbitration administered by JAMS under its Optional
            Expedited Arbitration Procedures then in effect (www.jamsadr.com).
            Unless we agree otherwise, arbitration will take place in
            Washington, D.C.
          </p>
          <p>
            If you use the Products for commercial purposes, each party pays its
            own JAMS filing, administrative, and arbitrator fees in accordance
            with JAMS rules, and the arbitrator's award will include arbitration
            costs, reasonable attorneys' fees, and reasonable expert and witness
            costs. If you are a private individual using the Products for
            non-commercial purposes: you may be required to pay a filing fee
            unless you request and are granted a fee waiver from JAMS; the
            arbitrator's award may include arbitration costs and reasonable
            attorneys', expert, and witness costs; and you may bring suit in a
            competent small-claims court without first engaging in arbitration,
            though this does not relieve you of the informal dispute-resolution
            step. Judgment on the award may be entered in any court of competent
            jurisdiction. Nothing in this section prevents us from seeking
            injunctive or equitable relief from the courts to prevent actual or
            threatened infringement or misappropriation of our data security,
            intellectual property, or other proprietary rights.
          </p>
          <p style={s.legalBlock}>
            <strong>Class action and jury trial waiver.</strong> ALL CLAIMS MUST
            BE BROUGHT IN THE PARTIES' INDIVIDUAL CAPACITY, AND NOT AS A
            PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION, COLLECTIVE
            ACTION, PRIVATE ATTORNEY GENERAL ACTION, OR OTHER REPRESENTATIVE
            PROCEEDING. THIS WAIVER APPLIES TO CLASS ARBITRATION, AND THE
            ARBITRATOR MAY NOT CONSOLIDATE MORE THAN ONE PERSON'S CLAIMS UNLESS
            WE AGREE OTHERWISE. YOU AND {COMPANY.shortName.toUpperCase()} EACH
            WAIVE THE RIGHT TO A JURY TRIAL AND THE RIGHT TO PARTICIPATE IN A
            CLASS ACTION, COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION, OR
            OTHER REPRESENTATIVE PROCEEDING RELATING TO THE PRODUCTS OR THESE
            TERMS.
          </p>
          <p>
            <strong>Limited applicability.</strong> Certain laws may prevent you
            from agreeing to binding arbitration in advance, waiving your right
            to participate in a representative proceeding, or waiving your right
            to a jury trial. If those laws apply to you, the arbitration
            requirement and the class action/jury trial waiver above do not
            apply to you.
          </p>
          <LegalFlag>
            Preserved verbatim from your 2022 Terms, including New York
            governing law with Washington, D.C. venue — an unusual combination
            for a Virginia LLC. Ask counsel whether that was deliberate or
            inherited from a template. Note also that an arbitration clause
            only holds up if it was conspicuously presented; the notice at the
            top of this page and the acceptance checkbox at signup exist for
            that reason.
          </LegalFlag>
        </Section>

        <Section n="18" title="General">
          <p>
            <strong>Changes.</strong> These Terms are subject to occasional
            revision. If we make substantial changes we may notify you by
            posting notice prominently on the site. Changes take effect on the
            earlier of thirty (30) days after we send an email notice or thirty
            (30) days after we post notice, and immediately for new users.
            Continued use after that indicates your agreement. The version and
            effective date appear at the top of this document.
          </p>
          <p>
            <strong>Severability and waiver.</strong> Our failure to enforce any
            right or provision is not a waiver of it. If any provision is found
            invalid or unenforceable it will be limited or eliminated to the
            minimum extent necessary and the remainder stays in force — except
            that if the class action and jury trial waiver in Section 17 is
            found unenforceable, the entire arbitration agreement is
            unenforceable.
          </p>
          <p>
            <strong>Entire agreement and assignment.</strong> These Terms,
            together with the Privacy Policy, are the final, complete, and
            exclusive agreement between you and {COMPANY.shortName} on these
            subjects, superseding all prior discussions and agreements including
            any earlier End User Licence Agreement, Terms of Service, or Terms
            of Use. Section titles are for convenience only. "Including" means
            "including without limitation". Your relationship to us is that of
            an independent contractor; neither party is the agent or partner of
            the other. You may not assign these Terms without our prior written
            consent, and any attempted assignment in violation of this is void.
            We may assign these Terms in connection with a merger, acquisition,
            reorganisation, or sale of assets without your consent.
          </p>
        </Section>

        <Section n="19" title="Contact">
          <ul style={s.contactList}>
            <li>
              <strong>Mail:</strong> {COMPANY.address}
            </li>
            <li>
              <strong>General:</strong>{" "}
              <a href={`mailto:${COMPANY.email}`} style={s.link}>
                {COMPANY.email}
              </a>
            </li>
            <li>
              <strong>Legal:</strong>{" "}
              <a href="mailto:SBLCounsel@smartboardlearning.com" style={s.link}>
                SBLCounsel@smartboardlearning.com
              </a>
            </li>
            <li>
              <strong>Privacy:</strong>{" "}
              <a href="mailto:privacy@smartboardlearning.com" style={s.link}>
                privacy@smartboardlearning.com
              </a>
            </li>
            <li>
              <strong>Phone:</strong>{" "}
              <a href={`tel:${COMPANY.phoneHref}`} style={s.link}>
                {COMPANY.phone}
              </a>
            </li>
          </ul>
        </Section>

        <footer style={s.footer}>
          <p style={s.footerText}>
            © {new Date().getFullYear()} {COMPANY.legalName}. All rights
            reserved.
          </p>
        </footer>
      </article>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Section({ n, title, children, isNew = false }) {
  return (
    <section style={s.section}>
      <h2 style={s.h2}>
        <span style={s.num}>{n}.</span> {title}
        {isNew && process.env.NODE_ENV !== "production" && (
          <span style={s.newBadge}>new</span>
        )}
      </h2>
      <div style={s.body}>{children}</div>
    </section>
  );
}

/** Visible to you in development only — never ships to customers. */
function LegalFlag({ children }) {
  if (process.env.NODE_ENV === "production") return null;
  return (
    <div style={s.flag}>
      <strong style={s.flagLabel}>Review with counsel:</strong> {children}
    </div>
  );
}

const s = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    padding: "40px 20px 80px",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  },
  sheet: {
    maxWidth: 800,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    padding: "48px 52px 40px",
    color: "#1e293b",
    lineHeight: 1.65,
  },
  header: { borderBottom: "1px solid #e2e8f0", paddingBottom: 24 },
  h1: { margin: 0, fontSize: 34, fontWeight: 800, color: "#0f172a" },
  meta: { margin: "8px 0 16px", color: "#64748b", fontSize: 13, fontWeight: 600 },
  notice: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "12px 16px",
    fontSize: 14,
    margin: "0 0 16px",
  },
  lede: { margin: 0, fontSize: 16, color: "#334155" },
  section: { marginTop: 34 },
  h2: {
    fontSize: 19,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  num: { color: "#1d4ed8" },
  newBadge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: "#dcfce7",
    color: "#166534",
    borderRadius: 999,
    padding: "2px 8px",
  },
  body: { fontSize: 15.5, color: "#334155" },
  list: { paddingLeft: 22, margin: "10px 0" },
  contactList: { listStyle: "none", padding: 0, margin: "10px 0" },
  address: {
    fontStyle: "normal",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "14px 18px",
    margin: "12px 0",
    fontSize: 14.5,
  },
  callout: {
    background: "#fffbeb",
    borderLeft: "4px solid #f59e0b",
    padding: "14px 18px",
    borderRadius: "0 8px 8px 0",
    margin: "16px 0",
  },
  legalBlock: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "14px 18px",
    fontSize: 13.5,
    letterSpacing: "0.01em",
    margin: "12px 0",
  },
  flag: {
    background: "#eff6ff",
    border: "1px dashed #93c5fd",
    borderRadius: 8,
    padding: "12px 16px",
    margin: "14px 0",
    fontSize: 13.5,
    color: "#1e40af",
  },
  flagLabel: { display: "block", marginBottom: 2 },
  link: { color: "#1d4ed8", fontWeight: 600 },
  footer: { marginTop: 44, paddingTop: 22, borderTop: "1px solid #e2e8f0" },
  footerText: { margin: "4px 0", fontSize: 14, color: "#64748b" },
};
